import * as _ from 'lodash-es';
import SimplePattern from './SimplePattern';

export const firstResult = <T, K>(
  array: T[],
  mapper: (item: T) => K,
): K | null => {
  for (let i = 0; i < array.length; i += 1) {
    const item = array[i];
    const value = item && mapper(item);
    if (value) {
      return value;
    }
  }
  return null;
};

export interface RawRule {
  host: string;
  path: string;
  queries?: Record<string, string>;
  hash?: string;
}
interface CompiledRule {
  host: SimplePattern;
  path: SimplePattern;
  queries?: { [key: string]: SimplePattern };
  hash?: SimplePattern;
}

class UrlRule {
  private readonly compiledRule: CompiledRule;

  constructor(rawRule: RawRule) {
    const host = new SimplePattern(rawRule.host, {
      delimiter: '.',
      strict: true,
    });
    const path = new SimplePattern(rawRule.path, {
      delimiter: '/',
      end: false,
      encode: encodeURI,
    });

    const compiledRule: CompiledRule = { host, path };
    if (rawRule.queries) {
      const queries: { [key: string]: SimplePattern } = {};
      Object.entries(rawRule.queries).forEach(([key, value]) => {
        queries[key] = new SimplePattern(value, { delimiter: '\n' });
      });
      compiledRule.queries = queries;
    }
    if (rawRule.hash) {
      compiledRule.hash = new SimplePattern(rawRule.hash, { delimiter: '\n' });
    }

    this.compiledRule = compiledRule;
  }

  match(url: URL): Record<string, string> | null {
    const { compiledRule } = this;
    const dict: Record<string, string> = {};

    const hostMatched = this.matchHost(url.host);
    if (!hostMatched) return null;
    Object.assign(dict, hostMatched);

    const pathMatched = compiledRule.path.match(url.pathname);
    if (!pathMatched) return null;
    Object.assign(dict, _.mapValues(pathMatched, decodeURIComponent));

    if (compiledRule.queries) {
      const keys = Object.keys(compiledRule.queries);
      const queriesMatched = {};
      for (let i = 0; i < keys.length; i += 1) {
        const name = keys[i]!;
        const pattern = compiledRule.queries[name]!;
        const param = url.searchParams.get(name);
        const matched = pattern.match(param || '');
        if (!matched) return null;
        Object.assign(queriesMatched, matched);
      }
      Object.assign(dict, queriesMatched);
    }

    if (compiledRule.hash) {
      const hashMatched = compiledRule.hash.match(
        url.hash ? url.hash.slice(1) : '',
      );
      if (!hashMatched) return null;
      Object.assign(dict, _.mapValues(hashMatched, decodeURIComponent));
    }

    return dict;
  }

  private matchHost(host: string) {
    return (
      this.compiledRule.host.match(host) ||
      this.compiledRule.host.match(`.${host}`)
    );
  }

  testHost(host: string): boolean {
    return !!this.matchHost(host);
  }

  format(dict: Record<string, string>) {
    try {
      const { host, path, queries, hash } = this.compiledRule;
      let url = `https://${_.trimStart(host.format(dict), '.')}${path.format(
        dict,
      )}`;

      if (queries) {
        const params = new URLSearchParams();
        Object.entries(queries).forEach(([name, pattern]) => {
          const formatted = pattern.format(dict);
          if (formatted) {
            params.set(name, formatted);
          }
        });
        const paramsString = params.toString();
        if (paramsString) {
          url += `?${paramsString}`;
        }
      }

      if (hash) {
        url += `#${hash.format(dict)}`;
      }

      return url;
    } catch (err) {
      return null;
    }
  }
}

export default UrlRule;
